import * as THREE from 'three'
import { GSplatManager, type GSplatManagerOptions } from './GSplatManager'
import type { Layer } from './Layer'
import type { LayerComposition } from './LayerComposition'

export interface GSplatDirectorOptions {
  managerOptions?: GSplatManagerOptions
}

/**
 * 事件映射类型
 */
export interface GSplatDirectorEvents {
  /**
   * Manager 创建时触发（可用于自定义材质）
   * @param manager - GSplatManager 实例
   * @param camera - Three.js 相机
   * @param layer - Layer 实例
   */
  'manager:created': (manager: GSplatManager, camera: THREE.Camera, layer: Layer) => void

  /**
   * 每帧渲染准备完成（用于加载进度显示）
   * @param camera - Three.js 相机
   * @param layer - Layer 实例
   * @param isReady - 是否所有资源已加载
   * @param loadingCount - 正在加载的文件数量
   */
  'frame:ready': (
    camera: THREE.Camera,
    layer: Layer,
    isReady: boolean,
    loadingCount: number
  ) => void
}

/**
 * Per Layer 数据
 *
 * @internal
 */
class GSplatLayerData {
  /**
   * GSplat Manager（管理此相机+Layer的渲染）
   */
  manager: GSplatManager | null = null

  /**
   * 创建 Manager
   *
   * @param renderer - WebGL 渲染器
   * @param director - Director 实例
   * @param layer - Layer 实例
   * @param camera - Three.js 相机
   */
  createManager(
    renderer: THREE.WebGLRenderer,
    director: GSplatDirector,
    layer: Layer,
    camera: THREE.Camera
  ): void {
    // 创建 Manager（使用相机作为 cameraNode）
    this.manager = new GSplatManager(renderer, camera, undefined, director.managerOptions)

    // 触发 'manager:created' 事件（允许外部自定义材质）
    director.dispatchEvent({ type: 'manager:created', manager: this.manager, camera, layer })
  }

  /**
   * 更新配置（根据 Layer 的 Placements 列表）
   *
   * @param renderer - WebGL 渲染器
   * @param director - Director 实例
   * @param layer - Layer 实例
   * @param camera - Three.js 相机
   */
  updateConfiguration(
    renderer: THREE.WebGLRenderer,
    director: GSplatDirector,
    layer: Layer,
    camera: THREE.Camera
  ): void {
    const hasNormalPlacements = layer.gsplatPlacements.length > 0

    // 简化方案：只支持普通渲染（无阴影）
    const desiredMainMode = hasNormalPlacements

    // 创建或销毁 Manager
    if (desiredMainMode && !this.manager) {
      this.createManager(renderer, director, layer, camera)
    } else if (!desiredMainMode && this.manager) {
      this.manager.destroy()
      this.manager = null
    }
  }

  /**
   * 销毁 LayerData
   */
  destroy(): void {
    if (this.manager) {
      this.manager.destroy()
      this.manager = null
    }
  }
}

/**
 * Per Camera 数据
 *
 * @internal
 */
class GSplatCameraData {
  /**
   * Layer ID 到 LayerData 的映射
   */
  layersMap: Map<number, GSplatLayerData> = new Map()

  /**
   * 销毁 CameraData
   */
  destroy(): void {
    this.layersMap.forEach((layerData) => layerData.destroy())
    this.layersMap.clear()
  }

  /**
   * 移除指定 Layer 的数据
   *
   * @param layerId - Layer ID
   */
  removeLayerData(layerId: number): void {
    const layerData = this.layersMap.get(layerId)
    if (layerData) {
      layerData.destroy()
      this.layersMap.delete(layerId)
    }
  }

  /**
   * 获取或创建 LayerData
   *
   * @param renderer - WebGL 渲染器
   * @param director - Director 实例
   * @param layer - Layer 实例
   * @param camera - Three.js 相机
   * @returns LayerData 实例
   */
  getLayerData(
    renderer: THREE.WebGLRenderer,
    director: GSplatDirector,
    layer: Layer,
    camera: THREE.Camera
  ): GSplatLayerData {
    let layerData = this.layersMap.get(layer.id)
    if (!layerData) {
      layerData = new GSplatLayerData()
      layerData.updateConfiguration(renderer, director, layer, camera)
      this.layersMap.set(layer.id, layerData)
    }
    return layerData
  }
}

// ==================== 临时数组（复用以避免 GC） ====================
const tempLayersToRemove: number[] = []

/**
 * GSplatDirector - 总控制器
 *
 * 核心功能：
 * 1. **多相机支持** - 为每个相机独立管理 GSplatManager
 * 2. **多 Layer 支持** - 每个相机+Layer 组合拥有独立的 Manager
 * 3. **生命周期管理** - 自动创建/销毁 Manager（当相机/Layer 变化时）
 * 4. **事件系统** - 提供 'manager:created' 和 'frame:ready' 事件
 *
 * 工作流程：
 * ```
 * update(composition) {
 *   1. 清理不再使用的 camera/layer
 *   2. 为每个 camera 的每个 layer 创建或更新 Manager
 *   3. 根据 placement 集合决定是否共享 Manager（简化版：无共享）
 *   4. 触发 Manager 更新并发射 'frame:ready' 事件
 * }
 * ```
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatDirector 类（简化版）
 * 主要差异：
 * - 移除 Scene/Renderer/GraphicsDevice 依赖（使用 THREE.WebGLRenderer）
 * - 移除阴影支持（gsplatManagerShadow）
 * - 简化事件系统（使用 Node.js EventEmitter）
 * - 移除渲染模式标志（GSPLAT_FORWARD/GSPLAT_SHADOW）
 */
export class GSplatDirector extends THREE.EventDispatcher<{
  'manager:created': { manager: GSplatManager; camera: THREE.Camera; layer: Layer }
  'frame:ready': { camera: THREE.Camera; layer: Layer; isReady: boolean; loadingCount: number }
}> {
  /**
   * WebGL 渲染器
   */
  renderer: THREE.WebGLRenderer

  managerOptions: GSplatManagerOptions | undefined

  /**
   * Per Camera 数据映射
   */
  private camerasMap: Map<THREE.Camera, GSplatCameraData> = new Map()

  /** Host-provided logical frame that remains reusable by synchronous onBeforeRender callbacks. */
  private currentCooldownFrameId: number | null = null

  /** Identity of the host logical-frame counter. */
  private currentCooldownFrameSource: object | null = null

  /** Fallback logical-frame counter for standalone Director users. */
  private fallbackCooldownFrameId = 0

  /** Stable identity for the standalone logical-frame counter. */
  private readonly fallbackCooldownFrameSource = {}

  /** Invalidates older microtasks that would otherwise clear a newer logical frame. */
  private cooldownFrameGeneration = 0

  /**
   * 统计：总 Splat 数量（用于调试）
   * @internal
   */
  _gsplatCount: number = 0

  /**
   * 创建 GSplatDirector
   *
   * @param renderer - Three.js WebGL 渲染器
   */
  constructor(renderer: THREE.WebGLRenderer, options: GSplatDirectorOptions = {}) {
    super()
    this.renderer = renderer
    this.managerOptions = options.managerOptions
  }

  setManagerOptions(options: GSplatManagerOptions | undefined): void {
    this.managerOptions = options
    for (const manager of this.getAllManagers()) {
      manager.setOrderUploadSchedulerOptions(options?.orderUploadScheduler)
      manager.setInteractiveSortSchedulerConfig(options?.interactiveSortScheduler)
      manager.setOnSortResultReady(options?.onSortResultReady ?? null)
    }
  }

  /**
   * 销毁 Director，释放所有资源
   */
  destroy(): void {
    // 销毁所有 Manager
    this.camerasMap.forEach((cameraData) => cameraData.destroy())
    this.camerasMap.clear()
    this.currentCooldownFrameId = null
    this.currentCooldownFrameSource = null
    this.cooldownFrameGeneration++

    // 移除所有事件监听器（EventDispatcher 没有 removeAllListeners，需要清除各个类型）
    // 注意：Three.js EventDispatcher 没有直接的 removeAllListeners 方法
  }

  /**
   * 获取或创建 CameraData
   *
   * @param camera - Three.js 相机
   * @returns CameraData 实例
   */
  private getCameraData(camera: THREE.Camera): GSplatCameraData {
    let cameraData = this.camerasMap.get(camera)
    if (!cameraData) {
      cameraData = new GSplatCameraData()
      this.camerasMap.set(camera, cameraData)
    }
    return cameraData
  }

  /**
   * 更新 Director（主循环）
   *
   * @param comp - LayerComposition 实例
   *
   * @remarks
   * 核心算法（对应 PlayCanvas 的 update() 方法）：
   * 1. 清理不再使用的 camera/layer
   * 2. 为每个 camera 的每个 layer 创建或更新 Manager
   * 3. 触发 Manager 更新并统计 Splat 数量
   * 4. 发射 'frame:ready' 事件
   * 5. 清除所有 Layer 的 dirty 标志
   */
  update(comp: LayerComposition, frameId?: number, frameSource?: object): void {
    const hasHostFrameId = typeof frameId === 'number' && Number.isFinite(frameId)
    const cooldownFrameId = hasHostFrameId ? Math.floor(frameId) : ++this.fallbackCooldownFrameId
    const cooldownFrameSource = hasHostFrameId
      ? (frameSource ?? this)
      : this.fallbackCooldownFrameSource
    this.rememberCooldownFrame(cooldownFrameId, cooldownFrameSource)

    // ==================== 阶段 1: 清理不再使用的 camera/layer ====================

    this.camerasMap.forEach((cameraData, camera) => {
      // 检查相机是否还在 composition 中
      if (!comp.camerasSet.has(camera)) {
        // 相机已移除：销毁所有 LayerData
        cameraData.destroy()
        this.camerasMap.delete(camera)
      } else {
        // 相机仍存在：检查 Layer 是否被禁用或移除
        const cameraComponent = comp.getCameraComponent(camera)
        if (!cameraComponent) return

        // 收集需要移除的 Layer ID
        tempLayersToRemove.length = 0
        cameraData.layersMap.forEach((_layerData, layerId) => {
          const layer = comp.getLayerById(layerId)
          // 移除条件：Layer 不在相机的 Layer 列表中，或 Layer 被禁用
          if (!cameraComponent.layersSet.has(layerId) || !layer || !layer.enabled) {
            tempLayersToRemove.push(layerId)
          }
        })

        // 移除 LayerData
        for (let i = 0; i < tempLayersToRemove.length; i++) {
          cameraData.removeLayerData(tempLayersToRemove[i])
        }

        tempLayersToRemove.length = 0
      }
    })

    // ==================== 阶段 2: 为每个相机的每个 Layer 创建/更新 Manager ====================

    let gsplatCount = 0

    // 遍历所有相机
    for (let i = 0; i < comp.cameras.length; i++) {
      const cameraComponent = comp.cameras[i]
      const camera = cameraComponent.camera
      let cameraData = this.camerasMap.get(camera)

      // 遍历相机的所有 Layer
      const layerIds = cameraComponent.layers
      for (let j = 0; j < layerIds.length; j++) {
        const layerId = layerIds[j]
        const layer = comp.getLayerById(layerId)

        // 跳过禁用的 Layer
        if (!layer || !layer.enabled) {
          continue
        }

        // 检查 Layer 的 Placements 是否修改，或相机是新的
        const isDirty = layer.gsplatPlacementsDirty
        const isNewCamera = !cameraData
        if (isDirty || isNewCamera) {
          const hasNormalPlacements = layer.gsplatPlacements.length > 0
          if (!hasNormalPlacements) {
            // 无 Placements：移除 Manager
            if (cameraData) {
              cameraData.removeLayerData(layer.id)
            }
          } else {
            // 有 Placements：更新 Manager
            cameraData ??= this.getCameraData(camera)
            const layerData = cameraData.getLayerData(this.renderer, this, layer, camera)

            // 更新配置（创建/销毁 Manager）
            layerData.updateConfiguration(this.renderer, this, layer, camera)

            // 协调 Manager 的 Placements
            if (layerData.manager) {
              layerData.manager.reconcile(layer.gsplatPlacements)
            }
          }
        }
      }

      // ==================== 阶段 3: 更新所有 Manager ====================

      if (cameraData) {
        for (const layerData of cameraData.layersMap.values()) {
          if (layerData.manager) {
            gsplatCount += layerData.manager.update(cooldownFrameId, cooldownFrameSource)
          }
        }
      }
    }

    // ==================== 阶段 4: 更新统计和清除 dirty 标志 ====================

    // 更新统计
    this._gsplatCount = gsplatCount

    // 清除所有 Layer 的 dirty 标志
    for (let i = 0; i < comp.layerList.length; i++) {
      const layer = comp.layerList[i]
      layer.gsplatPlacementsDirty = false
    }

    // ==================== 阶段 5: 发射 'frame:ready' 事件 ====================

    // 为每个 camera+layer 发射事件
    this.camerasMap.forEach((cameraData, camera) => {
      cameraData.layersMap.forEach((layerData, layerId) => {
        const layer = comp.getLayerById(layerId)
        if (layer && layerData.manager) {
          const isReady = layerData.manager.isReady
          const loadingCount = layerData.manager.loadingCount
          this.dispatchEvent({ type: 'frame:ready', camera, layer, isReady, loadingCount })
        }
      })
    })
  }

  /**
   * 从渲染回调更新 Director。
   *
   * PCManager 的 Prepare 与 Three.js 的 onBeforeRender 位于同一个同步渲染任务中。这里复用
   * Prepare 提供的宿主逻辑帧，避免 WorkBuffer 等离屏 render 改写 Three.js render.frame 后把
   * 同一帧的 cooldown 重复推进。没有宿主 Prepare 的独立用法则自动创建一次逻辑帧。
   */
  updateForRender(comp: LayerComposition): void {
    if (this.currentCooldownFrameId !== null && this.currentCooldownFrameSource) {
      this.update(comp, this.currentCooldownFrameId, this.currentCooldownFrameSource)
      return
    }
    this.update(comp)
  }

  private rememberCooldownFrame(frameId: number, frameSource: object): void {
    this.currentCooldownFrameId = frameId
    this.currentCooldownFrameSource = frameSource
    const generation = ++this.cooldownFrameGeneration

    // Director/Manager 更新及 Three.js render 均为同步流程。任务结束后清除 token，避免没有
    // 新 Prepare 的后续独立渲染错误复用旧宿主帧。
    queueMicrotask(() => {
      if (this.cooldownFrameGeneration !== generation) {
        return
      }
      this.currentCooldownFrameId = null
      this.currentCooldownFrameSource = null
    })
  }

  // ==================== 公共接口 ====================

  /**
   * 获取指定相机+Layer的 Manager（用于材质绑定）
   *
   * @param camera - Three.js 相机
   * @param layer - Layer 实例
   * @returns GSplatManager 或 null
   */
  getManager(camera: THREE.Camera, layer: Layer): GSplatManager | null {
    const cameraData = this.camerasMap.get(camera)
    if (!cameraData) return null

    const layerData = cameraData.layersMap.get(layer.id)
    return layerData?.manager ?? null
  }

  /**
   * 获取所有活跃的 Manager（用于调试）。
   *
   * @returns 当前存在的 manager 数组，无活跃时返回空数组
   */
  getAllManagers(): GSplatManager[] {
    const managers: GSplatManager[] = []
    this.camerasMap.forEach((cameraData) => {
      cameraData.layersMap.forEach((layerData) => {
        if (layerData.manager) {
          managers.push(layerData.manager)
        }
      })
    })
    return managers
  }

  /**
   * 获取渲染的总 Splat 数量（用于调试）
   */
  get gsplatCount(): number {
    return this._gsplatCount
  }
}
